import AgentsIcon from '@/assets/icons/AgentIcon';
import AiContextIcon from '@/assets/icons/AiContextIcon';
import SignalsIcon from '@/assets/icons/SignalsIcon';
import { IMenuItem, IHeaderMenuItem } from '@/types/nav';
import { Activity, Settings, CheckSquare, Brain, MessageSquareText, UserSearch, Send, Phone, LayoutDashboard, LayoutTemplate, Layers, Target, CalendarClock } from 'lucide-react';
import { PATH } from './path';

// Roles that have full write access. Viewer (#2694) sees only Signals.
const FULL_ACCESS_ROLES = ['admin', 'member'];

// Marketing surfaces are not part of an SDR's daily loop (signals -> actions ->
// outreach). Reported by Sunder for Jumio SDRs, 2026-09-24. AI Context stays:
// SDRs add their own voice there.
const NOT_FOR_SDR: IMenuItem['hiddenForPersonas'] = ['sdr'];

export type NavPersona = NonNullable<IMenuItem['hiddenForPersonas']>[number];

/**
 * The persona that drives `hiddenForPersonas`: the viewer's own ui_persona, never a
 * view-as target, so a workspace admin drilling into an SDR keeps their own nav.
 * Workspace admins are NOT exempt — an admin who chose the SDR experience is an SDR.
 * Super admins are MarketBetter staff who configure these surfaces for customers.
 */
export function navViewerPersona(ws?: { isSdr?: boolean; isAe?: boolean; isSuperAdmin?: boolean } | null): NavPersona | null {
  if (!ws || ws.isSuperAdmin === true) return null;
  if (ws.isSdr === true) return 'sdr';
  if (ws.isAe === true) return 'ae';
  return null;
}

export function isHiddenForPersona(item: Pick<IMenuItem, 'hiddenForPersonas'>, persona: NavPersona | null): boolean {
  return persona !== null && item.hiddenForPersonas?.includes(persona) === true;
}

export const NAV_ITEMS: IMenuItem[] = [
  // AI Chat — top-level, cross-cutting feature (not in any section)
  {
    icon: MessageSquareText,
    label: 'Skills', // #3884 — reframed from "Chat with MB"; /chat route preserved
    href: PATH.CHAT,
    requiresWorkspace: true,
    allowedPlans: ['starter', 'professional', 'pro', 'enterprise'],
    allowedRoles: FULL_ACCESS_ROLES,
  },

  // GH#830: Analytics section (replaces Dashboard, absorbs Results)
  {
    label: 'ANALYTICS',
    icon: () => null,
    href: '#',
    isSectionHeader: true,
    sectionItems: [
      {
        label: 'Dashboard',
        icon: LayoutDashboard,
        href: PATH.ANALYTICS_OVERVIEW,
        requiresWorkspace: true,
        allowedPlans: ['starter', 'professional', 'pro', 'enterprise'],
        allowedRoles: FULL_ACCESS_ROLES,
      },
      {
        label: 'Performance',
        icon: Activity,
        href: PATH.ANALYTICS_ACTIVITY,
        requiresWorkspace: true,
        allowedPlans: ['starter', 'professional', 'pro', 'enterprise'],
        requiresFeature: 'outbound_enabled',
        requiredModule: 'sales',
        allowedRoles: FULL_ACCESS_ROLES,
      },
      // Revenue hidden from sidebar but route still accessible at /analytics/revenue
      // {
      //   label: 'Revenue',
      //   icon: DollarSign,
      //   href: PATH.ANALYTICS_REVENUE,
      //   requiresWorkspace: true,
      //   allowedPlans: ['professional', 'pro', 'enterprise'],
      // },
    ],
  },

  // INBOUND Section
  {
    label: 'INBOUND',
    icon: () => null, // No icon for section headers
    href: '#',
    isSectionHeader: true,
    sectionItems: [
      {
        icon: SignalsIcon,
        label: 'Signals',
        href: PATH.SIGNALS,
        requiresWorkspace: true,
        allowedPlans: ['starter', 'professional', 'pro', 'enterprise'],
        // Viewers can see Signals — no allowedRoles restriction (#2694).
        // The hub's views are its scope (mb-frontend#5192 §1): the sidebar lists them
        // as children so a rep lands on their own list, a manager on everyone's, and
        // the Unassigned and People lists are one click rather than a dropdown away.
        // Each is the same page with the `assignee` / `view` parameter the hub already
        // read, so every bookmark keeps working.
        children: [
          { icon: SignalsIcon, label: 'My signals', href: `${PATH.SIGNALS}?assignee=me`, requiresWorkspace: true },
          { icon: SignalsIcon, label: 'All SDRs', href: `${PATH.SIGNALS}?assignee=all`, requiresWorkspace: true },
          { icon: SignalsIcon, label: 'Unassigned', href: `${PATH.SIGNALS}?assignee=unassigned`, requiresWorkspace: true },
          { icon: SignalsIcon, label: 'People', href: `${PATH.SIGNALS}?view=people`, requiresWorkspace: true },
        ],
      },
      {
        icon: Brain,
        label: 'AI SEO',
        href: PATH.AI_SEO,
        requiresWorkspace: true,
        allowedPlans: ['enterprise'],
        requiresFeature: 'ai_seo_enabled',
        requiredModule: 'marketing',
        allowedRoles: FULL_ACCESS_ROLES,
        hiddenForPersonas: NOT_FOR_SDR,
      },
      {
        icon: AgentsIcon,
        label: 'Chat Bot',
        href: PATH.BOT,
        requiresWorkspace: true,
        allowedPlans: ['enterprise'],
        requiredModule: 'marketing',
        allowedRoles: FULL_ACCESS_ROLES,
        hiddenForPersonas: NOT_FOR_SDR,
      },
      {
        icon: UserSearch,
        label: 'Prospects',
        href: PATH.PROSPECTS,
        requiresWorkspace: true,
        requiredModule: 'sales',
        allowedRoles: FULL_ACCESS_ROLES,
      },
    ],
  },

  // Meetings — standalone, opt-in surface (#3080). Hidden unless meetings_enabled is true.
  // NOT nested under OUTBOUND: it's gated on its own flag, independent of outbound_enabled.
  {
    icon: CalendarClock,
    label: 'Meetings',
    href: PATH.MEETINGS,
    requiresWorkspace: true,
    hideWhenFeatureDisabled: 'meetings_enabled',
    requiredModule: 'sales',
    allowedRoles: FULL_ACCESS_ROLES,
  },

  // OUTBOUND Section — hidden entirely when outbound_enabled is false
  {
    label: 'OUTBOUND',
    icon: () => null,
    href: '#',
    isSectionHeader: true,
    hideWhenFeatureDisabled: 'outbound_enabled',
    sectionItems: [
      {
        icon: Layers,
        label: 'Opportunities',
        href: PATH.OPPORTUNITIES,
        requiresWorkspace: true,
        requiredModule: 'sales',
        allowedRoles: FULL_ACCESS_ROLES,
      },
      {
        icon: CheckSquare,
        label: 'Actions',
        href: PATH.TASKS,
        requiresWorkspace: true,
        allowedPlans: ['starter', 'professional', 'pro', 'enterprise'],
        requiresFeature: 'gtm_signal_enabled',
        requiredModule: 'sales',
        allowedRoles: FULL_ACCESS_ROLES,
      },
      {
        icon: Target,
        label: 'Audiences',
        href: PATH.AUDIENCES,
        requiresWorkspace: true,
        requiresFeature: 'outbound_enabled',
        requiredModule: 'sales',
        allowedRoles: FULL_ACCESS_ROLES,
      },
      {
        icon: Send,
        label: 'Campaigns',
        href: PATH.CAMPAIGNS,
        requiresWorkspace: true,
        requiresFeature: 'outbound_enabled',
        requiredModule: 'sales',
        allowedRoles: FULL_ACCESS_ROLES,
        // Requested by Krishna Natarajan via Sunder, 2026-10-02: the outreach Templates list was
        // only reachable as a tab once you were already on /campaigns, so nothing in the nav said
        // it existed. Same gates as Campaigns itself — the sidebar reaches a child only through a
        // parent that is visible and unlocked.
        children: [
          {
            icon: LayoutTemplate,
            label: 'Templates',
            href: PATH.CAMPAIGNS_OUTREACH_TEMPLATES,
            // The list is /campaigns?tab=templates; one template's own pages are their own routes.
            activePaths: ['/campaigns/outreach-templates'],
            requiresWorkspace: true,
            requiresFeature: 'outbound_enabled',
            requiredModule: 'sales',
            allowedRoles: FULL_ACCESS_ROLES,
          },
        ],
      },
      {
        icon: Phone,
        label: 'Power Dialer',
        href: PATH.POWER_DIALER,
        requiresWorkspace: true,
        requiresFeature: 'power_dialer_enabled',
        requiredModule: 'sales',
        allowedRoles: FULL_ACCESS_ROLES,
      },
    ],
  },

  // SETTINGS Section
  {
    label: 'SETTINGS',
    icon: () => null,
    href: '#',
    isSectionHeader: true,
    sectionItems: [
      {
        icon: AiContextIcon,
        label: 'AI Context',
        href: PATH.AI_CONTEXT,
        requiresWorkspace: true,
        allowedRoles: FULL_ACCESS_ROLES,
      },
      {
        icon: Settings,
        label: 'Settings',
        href: PATH.SETTINGS,
        requiresWorkspace: false, // Personal settings don't require workspace
        allowedRoles: FULL_ACCESS_ROLES,
      },
    ],
  },
];

/** Public marketing header — order matches Figma New LP (Home + Products dropdown rendered separately). */
export const NAV_HEADER_MENUS: IHeaderMenuItem[] = [
  {
    title: 'Home',
    href: PATH.HOME,
  },
  {
    title: 'Pricing',
    href: PATH.PRICING,
  },
  {
    title: 'Case Studies',
    href: PATH.CASE_STUDIES,
  },
  {
    title: 'Playbooks',
    href: '/playbooks',
  },
];
